import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { ColDef } from '@ag-grid-community/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, FileSpreadsheet, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import * as z from 'zod';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRegistrars } from '@/hooks/use-domains';
import type {
  BatchCalendarDomain,
  BatchCalendarResponse,
  BatchCalendarSummary,
  BatchCalendarParams,
} from '@/hooks/use-reports';
import { useBatchCalendarReport } from '@/hooks/use-reports';

const REPORT_PATH = '/app/reports/batch-calendar';

// Form validation schema
const formSchema = z.object({
  batch_by: z.enum(['registrant_email', 'license', 'registrant_org_name']),
  regions: z.array(z.string()).optional(),
  exclude_regions: z.array(z.string()).optional(),
  registrars: z.array(z.string()).optional(),
  exclude_registrars: z.array(z.string()).optional(),
  skip_rdds_check: z.boolean().default(false),
  skip_noavsend: z.boolean().default(false),
  include_failed_verifications: z.boolean().default(false),
  activity_window_days: z.preprocess((value) => {
    if (value === '' || value === null || value === undefined)
      return;
    if (typeof value === 'number')
      return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
  }, z.number().int().min(0).max(365).optional()),
});

type BatchCalendarFilters = z.infer<typeof formSchema>;

// Batch criteria options
const BATCH_CRITERIA = [
  { value: 'registrant_email', label: 'Registrant Email' },
  { value: 'license', label: 'License' },
  { value: 'registrant_org_name', label: 'Organization Name' },
] as const;

// Region options
const REGIONS = [
  { value: 'US', label: 'US' },
  { value: 'EU-EEA', label: 'EU-EEA' },
  { value: 'ASIA', label: 'ASIA' },
  { value: 'INT', label: 'INT' },
  { value: 'INT excluding ASIA', label: 'INT excluding ASIA' },
  { value: 'INT excluding EU-EEA', label: 'INT excluding EU-EEA' },
];

// Helper to format headers for display
const formatHeader = (header: string) => header.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());

// Column Definitions for Batch Calendar Domains
function batchCalendarColDefs(): ColDef<BatchCalendarDomain>[] {
  return [
    {
      field: 'domain_name',
      headerName: 'Domain',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
      pinned: 'left',
    },
    {
      field: 'tld',
      headerName: 'TLD',
      filter: true,
      sortable: true,
      resizable: true,
      width: 120,
    },
    {
      field: 'batch_criteria',
      headerName: 'Batch Criteria',
      filter: true,
      sortable: true,
      resizable: true,
      width: 250,
    },
    {
      field: 'batch_criteria_type',
      headerName: 'Criteria Type',
      filter: true,
      sortable: true,
      resizable: true,
      width: 150,
      valueFormatter: (params: { value: string }) => {
        switch (params.value) {
          case 'registrant_email': { return 'Registrant Email'; }
          case 'license': { return 'License'; }
          case 'registrant_org_name': { return 'Organization Name'; }
          default: { return params.value; }
        }
      },
    },
    {
      field: 'batch_size',
      headerName: 'Batch Size',
      filter: 'agNumberColumnFilter',
      sortable: true,
      resizable: true,
      width: 120,
    },
    {
      field: 'oldest_verification',
      headerName: 'Oldest Verification',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 160,
      sort: 'asc',
      sortIndex: 0,
      valueFormatter: (params: { value: string }) => {
        if (!params.value)
          return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'last_verified',
      headerName: 'Last Verified',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 140,
      valueFormatter: (params: { value: string | null }) => {
        if (!params.value)
          return 'Never';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'last_deal_created_at',
      headerName: 'Last Deal Create Date',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 180,
      valueFormatter: (params: { value: string | null }) => {
        if (!params.value)
          return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'deal_stage',
      headerName: 'Deal Stage',
      filter: true,
      sortable: true,
      resizable: true,
      width: 220,
    },
    {
      field: 'deal_status_by_stage',
      headerName: 'Deal Status (Stage)',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      field: 'domain_status',
      headerName: 'Domain Status',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      field: 'domain_status_raw',
      headerName: 'Domain Status (Raw)',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'deal_last_updated_at',
      headerName: 'Deal Last Updated',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 180,
      valueFormatter: (params: { value: string | null }) => {
        if (!params.value)
          return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'most_recent_registrar',
      headerName: 'Registrar',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'noavsend',
      headerName: 'No AV Send',
      filter: true,
      sortable: true,
      resizable: true,
      width: 120,
      valueFormatter: (params: { value: boolean }) => params.value ? 'Yes' : 'No',
    },
    {
      field: 'use_restriction',
      headerName: 'Use Restriction',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'registrant_legal_name',
      headerName: 'Registrant Name',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      field: 'registrant_alt_name',
      headerName: 'Registrant Alt Name',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      field: 'registrant_email',
      headerName: 'Registrant Email',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'registrant_alt_email',
      headerName: 'Registrant Alt Email',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'registrant_organization',
      headerName: 'Registrant Organization',
      filter: true,
      sortable: true,
      resizable: true,
      width: 250,
    },
    {
      field: 'registrant_country',
      headerName: 'Registrant Country',
      filter: true,
      sortable: true,
      resizable: true,
      width: 150,
    },
    {
      field: 'verification_legal_name',
      headerName: 'Verification Name',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      field: 'verification_alt_name',
      headerName: 'Verification Alt Name',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
    },
    {
      field: 'verification_email',
      headerName: 'Verification Email',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'verification_alt_email',
      headerName: 'Verification Alt Email',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
    {
      field: 'verification_organization',
      headerName: 'Verification Organization',
      filter: true,
      sortable: true,
      resizable: true,
      width: 250,
    },
    {
      field: 'fdic_licenses',
      headerName: 'FDIC Licenses',
      filter: true,
      sortable: true,
      resizable: true,
      width: 150,
    },
    {
      field: 'other_licenses',
      headerName: 'Other Licenses',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
    },
  ];
}

// Column Definitions for Batch Summary
function batchSummaryColDefs(): ColDef<BatchCalendarSummary>[] {
  return [
    {
      field: 'criteria',
      headerName: 'Batch Criteria',
      filter: true,
      sortable: true,
      resizable: true,
      width: 220,
      pinned: 'left',
    },
    {
      field: 'criteria_type',
      headerName: 'Criteria Type',
      filter: true,
      sortable: true,
      resizable: true,
      width: 150,
      valueFormatter: (params: { value: string }) => {
        switch (params.value) {
          case 'registrant_email': { return 'Registrant Email'; }
          case 'license': { return 'License'; }
          case 'registrant_org_name': { return 'Organization Name'; }
          default: { return params.value; }
        }
      },
    },
    {
      field: 'domain_count',
      headerName: 'Domain Count',
      filter: 'agNumberColumnFilter',
      sortable: true,
      resizable: true,
      width: 130,
    },
    {
      field: 'oldest_verification',
      headerName: 'Oldest Verification',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 160,
      valueFormatter: (params: { value: string }) => {
        if (!params.value)
          return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'domains',
      headerName: 'Domains (truncated)',
      filter: true,
      sortable: true,
      resizable: true,
      width: 280,
      valueGetter: (params: { data: BatchCalendarSummary | undefined }) => {
        if (!params.data)
          return '';
        const joined = params.data.domains.join(', ');
        return joined.length > 50 ? `${joined.slice(0, 50)}…` : joined;
      },
      tooltipValueGetter: (params: any) => params?.data?.domains ? params.data.domains.join(', ') : '',
    },
    {
      field: 'regions',
      headerName: 'Regions',
      filter: true,
      sortable: true,
      resizable: true,
      width: 160,
      valueGetter: (params: { data: BatchCalendarSummary | undefined }) => params.data ? params.data.regions.join(', ') : '',
    },
    {
      field: 'deal_stage',
      headerName: 'Deal Stage',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
      valueGetter: (params: { data: BatchCalendarSummary | undefined }) => params.data?.deal_stage ?? '',
    },
    {
      field: 'deal_status_by_stage',
      headerName: 'Deal Status (Stage)',
      filter: true,
      sortable: true,
      resizable: true,
      width: 180,
      valueGetter: (params: { data: BatchCalendarSummary | undefined }) => params.data?.deal_status_by_stage ?? '',
    },
    {
      field: 'domain_status',
      headerName: 'Domain Status',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
      valueGetter: (params: { data: BatchCalendarSummary | undefined }) => params.data?.domain_status ?? '',
    },
  ];
}

// Discrepancy row type and columns
type DiscrepancyRow = {
  domain_name: string;
  in_batches: boolean;
  batch_criteria: string | null;
  batch_criteria_type: 'registrant_email' | 'license' | 'registrant_org_name' | null;
  baseline_last_verified: string | null;
  algorithm_last_verified: string | null;
  delta_days: number | null;
};

function discrepancyColDefs(): ColDef<DiscrepancyRow>[] {
  return [
    {
      field: 'domain_name',
      headerName: 'Domain',
      filter: true,
      sortable: true,
      resizable: true,
      width: 200,
      pinned: 'left',
    },
    {
      field: 'in_batches',
      headerName: 'In Batches',
      filter: 'agTextColumnFilter',
      sortable: true,
      resizable: true,
      width: 120,
      valueFormatter: (p: { value: boolean }) => (p.value ? 'Yes' : 'No'),
    },
    {
      field: 'batch_criteria',
      headerName: 'Batch Criteria',
      filter: true,
      sortable: true,
      resizable: true,
      width: 260,
    },
    {
      field: 'batch_criteria_type',
      headerName: 'Criteria Type',
      filter: true,
      sortable: true,
      resizable: true,
      width: 160,
      valueFormatter: (params: { value: string | null }) => {
        switch (params.value) {
          case 'registrant_email': { return 'Registrant Email'; }
          case 'license': { return 'License'; }
          case 'registrant_org_name': { return 'Organization Name'; }
          case null: { return ''; }
          default: { return params.value || ''; }
        }
      },
    },
    {
      field: 'baseline_last_verified',
      headerName: 'Baseline Last Verified',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 180,
      valueFormatter: (p: { value: string | null }) => p.value ? new Date(p.value).toLocaleDateString() : 'Never',
    },
    {
      field: 'algorithm_last_verified',
      headerName: 'Algorithm Last Verified',
      filter: 'agDateColumnFilter',
      sortable: true,
      resizable: true,
      width: 200,
      valueFormatter: (p: { value: string | null }) => p.value ? new Date(p.value).toLocaleDateString() : '',
    },
    {
      field: 'delta_days',
      headerName: 'Delta (days)',
      filter: 'agNumberColumnFilter',
      sortable: true,
      resizable: true,
      width: 140,
    },
  ];
}

// Define headers for Excel export
const batchCalendarHeadersExcel: (keyof BatchCalendarDomain)[] = [
  'domain_name',
  'tld',
  'batch_criteria',
  'batch_criteria_type',
  'batch_size',
  'oldest_verification',
  'last_verified',
  'last_deal_created_at',
  'deal_stage',
  'deal_status_by_stage',
  'domain_status',
  'domain_status_raw',
  'deal_last_updated_at',
  'most_recent_registrar',
  'noavsend',
  'use_restriction',
  'use_restriction_note',
  'registrant_legal_name',
  'registrant_alt_name',
  'registrant_email',
  'registrant_alt_email',
  'registrant_phone',
  'registrant_organization',
  'registrant_country',
  'registrant_state_province',
  'verification_legal_name',
  'verification_alt_name',
  'verification_email',
  'verification_alt_email',
  'verification_phone',
  'verification_organization',
  'verification_country',
  'verification_state_province',
  'fdic_licenses',
  'holding_company_licenses',
  'other_licenses',
];

// Pre-process data for Excel
function processBatchCalendarDataForExcel(row: BatchCalendarDomain) {
  const processed: Record<string, string | number | boolean> = {};

  for (const header of batchCalendarHeadersExcel) {
    const value = row[header];
    if (value === null || value === undefined) {
      processed[header] = '';
    } else if (typeof value === 'boolean') {
      processed[header] = value ? 'Yes' : 'No';
    } else {
      // Pass all other values through as-is, including dates which are already formatted
      processed[header] = value;
    }
  }

  return processed;
}

// Get Max Length for Column Width Calculation
function getMaxLength(arr: any[], key: string) {
  return Math.max(0, ...arr.map((item) => (item[key]?.toString() ?? '').length));
}

export default function BatchCalendarReport() {
  const { setLabel, removeLabel } = useBreadcrumb();
  const { mutateAsync, isPending, reset } = useBatchCalendarReport();
  const { data: registrarList } = useRegistrars();
  const [reportData, setReportData] = useState<BatchCalendarResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Memoize column definitions
  const domainColumns = useMemo(() => batchCalendarColDefs(), []);
  const summaryColumns = useMemo(() => batchSummaryColDefs(), []);
  const discrepancyColumns = useMemo(() => discrepancyColDefs(), []);

  // Form setup
  const form = useForm<BatchCalendarFilters>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      batch_by: 'registrant_email',
      regions: [],
      exclude_regions: [],
      registrars: [],
      exclude_registrars: [],
      skip_rdds_check: false,
      skip_noavsend: false,
      include_failed_verifications: false,
      activity_window_days: 7,
    },
  });

  const registrarOptions = registrarList
    ? registrarList.map((registrar) => ({ value: registrar, label: registrar }))
    : [];

  useEffect(() => {
    setLabel(REPORT_PATH, {
      name: 'Batch Calendar Report',
      path: REPORT_PATH,
    });
    return () => removeLabel(REPORT_PATH);
  }, [setLabel, removeLabel]);

  // Compute discrepancies between algorithm output and baseline domain list
  const discrepancyRows = useMemo<DiscrepancyRow[]>(() => {
    if (!reportData)
      return [];

    const algoMap = new Map(reportData.domains.map((d) => [d.domain_name.toLowerCase(), d]));
    const baselineSetRaw = new Set(reportData.all_domains.map((b) => b.domain_name.toLowerCase()));
    const rows: DiscrepancyRow[] = [];

    // Domains from baseline (all_domains)
    for (const base of reportData.all_domains) {
      const key = base.domain_name.toLowerCase();
      if (algoMap.has(key))
        continue;

      const baselineLV = base.last_verified;

      rows.push({
        domain_name: base.domain_name,
        in_batches: false,
        batch_criteria: null,
        batch_criteria_type: null,
        baseline_last_verified: baselineLV,
        algorithm_last_verified: null,
        delta_days: null,
      });
    }

    // Any domains present in algorithm but missing from baseline (unlikely but possible)
    for (const algo of reportData.domains) {
      const key = algo.domain_name.toLowerCase();
      if (!baselineSetRaw.has(key)) {
        rows.push({
          domain_name: algo.domain_name,
          in_batches: true,
          batch_criteria: algo.batch_criteria,
          batch_criteria_type: algo.batch_criteria_type,
          baseline_last_verified: null,
          algorithm_last_verified: algo.last_verified,
          delta_days: null,
        });
      }
    }

    // Sort by in_batches (missing first), then by domain
    rows.sort((a, b) => {
      if (a.in_batches !== b.in_batches)
        return a.in_batches ? 1 : -1;
      return a.domain_name.localeCompare(b.domain_name);
    });

    return rows;
  }, [reportData]);

  // Generate Report function
  const handleGenerateReport = async (data: BatchCalendarFilters) => {
    reset();
    setReportData(null);

    const params: BatchCalendarParams = {
      batch_by: data.batch_by,
      filters: {
        regions: data.regions?.length ? data.regions : undefined,
        exclude_regions: data.exclude_regions?.length ? data.exclude_regions : undefined,
        registrars: data.registrars?.length ? data.registrars : undefined,
        exclude_registrars: data.exclude_registrars?.length ? data.exclude_registrars : undefined,
        skip_rdds_check: data.skip_rdds_check,
        skip_noavsend: data.skip_noavsend,
        include_failed_verifications: data.include_failed_verifications,
        activity_window_days: data.activity_window_days ?? undefined,
      },
    };

    const generationPromise = mutateAsync(params);

    toast.promise(generationPromise, {
      loading: 'Generating batch calendar report...',
      success: (responseData) => {
        setReportData(responseData);
        return `Report generated successfully! Found ${responseData.total_domains} domains in ${responseData.total_batches} batches.`;
      },
      error: (err) => {
        return err instanceof Error ? err.message : 'Failed to generate report.';
      },
    });
  };

  // Download Excel function
  const handleDownloadExcel = () => {
    if (!reportData) {
      toast.error('No report data available to download.');
      return;
    }

    try {
      // Process data for Excel
      const processedDomains = reportData.domains.map((domain) => processBatchCalendarDataForExcel(domain));

      // Create worksheet headers
      const excelHeaders = batchCalendarHeadersExcel.map((header) => formatHeader(header));

      const ws = utils.json_to_sheet(processedDomains, {
        header: batchCalendarHeadersExcel,
      });

      // Add formatted headers
      utils.sheet_add_aoa(ws, [excelHeaders], { origin: 'A1' });

      // Auto-size columns with fixed width for use_restriction_note
      const colWidths = excelHeaders.map((header, index) => {
        const key = batchCalendarHeadersExcel[index];

        // Set fixed width for use_restriction_note column
        if (key === 'use_restriction_note') {
          return { wch: 50 }; // Fixed width of 50 characters
        }

        return {
          wch: Math.max(header.length, getMaxLength(processedDomains, key), 10) + 2,
        };
      });
      ws['!cols'] = colWidths;

      // Create workbook and trigger download
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Batch Calendar');

      // Add batch summary sheet (convert arrays to strings and format dates)
      const summaryHeaders: string[] = [
        'criteria',
        'criteria_type',
        'domain_count',
        'oldest_verification',
        'domains',
        'regions',
        'deal_stage',
        'deal_status_by_stage',
        'domain_status',
      ];
      const summaryExport = reportData.batch_summary.map((s) => ({
        criteria: s.criteria,
        criteria_type: (() => {
          switch (s.criteria_type) {
            case 'registrant_email': { return 'Registrant Email'; }
            case 'license': { return 'License'; }
            case 'registrant_org_name': { return 'Organization Name'; }
            default: { return s.criteria_type; }
          }
        })(),
        domain_count: s.domain_count,
        oldest_verification: s.oldest_verification ? new Date(s.oldest_verification).toLocaleDateString() : '',
        domains: (s.domains || []).join(', '),
        regions: (s.regions || []).join(', '),
        deal_stage: s.deal_stage ?? '',
        deal_status_by_stage: s.deal_status_by_stage ?? '',
        domain_status: s.domain_status ?? '',
      }));
      const summaryWs = utils.json_to_sheet(summaryExport, { header: summaryHeaders });
      // Add formatted headers
      utils.sheet_add_aoa(summaryWs, [summaryHeaders.map((h) => formatHeader(h))], { origin: 'A1' });
      // Column widths (set domains to 50 characters)
      const summaryColWidths = summaryHeaders.map((header) => {
        if (header === 'domains')
          return { wch: 50 };
        if (header === 'regions')
          return { wch: 20 };
        if (header === 'deal_stage')
          return { wch: 20 };
        if (header === 'deal_status_by_stage' || header === 'deal_status_by_last_activity')
          return { wch: 26 };
        return { wch: Math.max(formatHeader(header).length, 12) };
      });
      (summaryWs as any)['!cols'] = summaryColWidths;
      utils.book_append_sheet(wb, summaryWs, 'Batch Summary');

      // Add discrepancies sheet
      const discrepancyExport = discrepancyRows.map((r) => ({
        Domain: r.domain_name,
        'In Batches': r.in_batches ? 'Yes' : 'No',
        'Batch Criteria': r.batch_criteria || '',
        'Criteria Type': r.batch_criteria_type || '',
        'Baseline Last Verified': r.baseline_last_verified ? new Date(r.baseline_last_verified).toLocaleDateString() : 'Never',
        'Algorithm Last Verified': r.algorithm_last_verified ? new Date(r.algorithm_last_verified).toLocaleDateString() : '',
        'Delta (days)': r.delta_days ?? '',
      }));
      const discrepancyWs = utils.json_to_sheet(discrepancyExport);
      utils.book_append_sheet(wb, discrepancyWs, 'Discrepancies');

      writeFile(wb, `batch_calendar_report_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success('Excel report download started!');
    } catch (excelError) {
      console.error('Error generating Excel file:', excelError);
      toast.error('Failed to create Excel file.');
    }
  };

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6">
        <h1 className="text-xl font-bold">Batch Calendar Report</h1>
        <p className="text-sm text-muted-foreground">
          Generate a comprehensive calendar of all verification batches showing detailed domain information, contacts, and licenses.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Generate Report</CardTitle>
          <CardDescription>
            This report runs the batching algorithm without limits to show verification batches for comprehensive planning.
            Each row represents a domain with its batch information, contacts, licenses, and verification details.
            By default, only recently updated domains (last 48 hours) are included - enable "Skip RDDS Check" to include all domains.
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleGenerateReport)}>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="batch_by"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Batch Criteria</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select batch criteria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BATCH_CRITERIA.map((criteria) => (
                            <SelectItem key={criteria.value} value={criteria.value}>
                              {criteria.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How domains should be grouped into batches.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="skip_rdds_check"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Skip RDDS Check
                          </FormLabel>
                          <FormDescription>
                            Include all domains instead of just recently updated ones.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="skip_noavsend"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Include No AV Send Domains
                          </FormLabel>
                          <FormDescription>
                            Include domains marked as "no annual verification send".
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="include_failed_verifications"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Include Failed Verifications
                          </FormLabel>
                          <FormDescription>
                            Include domains with failed verification status.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="activity_window_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deal Activity Window (days)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            max={365}
                            value={field.value ?? ''}
                            onChange={(event) => {
                              const value = event.target.value;
                              field.onChange(value === '' ? undefined : Number(value));
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Determines when a deal is considered active based on last update. Defaults to 7 days.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="regions"
                  render={() => (
                    <FormItem>
                      <FormLabel>Include Regions</FormLabel>
                      <div className="space-y-2">
                        {REGIONS.map((region) => (
                          <FormField
                            key={region.value}
                            control={form.control}
                            name="regions"
                            render={({ field }) => (
                              <FormItem
                                key={region.value}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(region.value)}
                                    onCheckedChange={(checked) => {
                                      const newValue = checked
                                        ? [...(field.value || []), region.value]
                                        : field.value?.filter((value) => value !== region.value) || [];
                                      field.onChange(newValue);
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {region.label}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                      <FormDescription>
                        Select specific regions to include (leave empty for all).
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="exclude_regions"
                  render={() => (
                    <FormItem>
                      <FormLabel>Exclude Regions</FormLabel>
                      <div className="space-y-2">
                        {REGIONS.map((region) => (
                          <FormField
                            key={region.value}
                            control={form.control}
                            name="exclude_regions"
                            render={({ field }) => (
                              <FormItem
                                key={region.value}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(region.value)}
                                    onCheckedChange={(checked) => {
                                      const newValue = checked
                                        ? [...(field.value || []), region.value]
                                        : field.value?.filter((value) => value !== region.value) || [];
                                      field.onChange(newValue);
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {region.label}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                      <FormDescription>
                        Select specific regions to exclude.
                      </FormDescription>
                    </FormItem>
                  )}
                />
              </div>

              {registrarOptions.length > 0 && (
                <>
                  <Separator />
                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="registrars"
                      render={() => (
                        <FormItem>
                          <FormLabel>Include Registrars</FormLabel>
                          <div className="max-h-48 space-y-2 overflow-y-auto">
                            {registrarOptions.map((registrar) => (
                              <FormField
                                key={registrar.value}
                                control={form.control}
                                name="registrars"
                                render={({ field }) => (
                                  <FormItem
                                    key={registrar.value}
                                    className="flex flex-row items-start space-x-3 space-y-0"
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(registrar.value)}
                                        onCheckedChange={(checked) => {
                                          const newValue = checked
                                            ? [...(field.value || []), registrar.value]
                                            : field.value?.filter((value) => value !== registrar.value) || [];
                                          field.onChange(newValue);
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="text-sm font-normal">
                                      {registrar.label}
                                    </FormLabel>
                                  </FormItem>
                                )}
                              />
                            ))}
                          </div>
                          <FormDescription>
                            Select specific registrars to include (leave empty for all).
                          </FormDescription>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="exclude_registrars"
                      render={() => (
                        <FormItem>
                          <FormLabel>Exclude Registrars</FormLabel>
                          <div className="max-h-48 space-y-2 overflow-y-auto">
                            {registrarOptions.map((registrar) => (
                              <FormField
                                key={registrar.value}
                                control={form.control}
                                name="exclude_registrars"
                                render={({ field }) => (
                                  <FormItem
                                    key={registrar.value}
                                    className="flex flex-row items-start space-x-3 space-y-0"
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(registrar.value)}
                                        onCheckedChange={(checked) => {
                                          const newValue = checked
                                            ? [...(field.value || []), registrar.value]
                                            : field.value?.filter((value) => value !== registrar.value) || [];
                                          field.onChange(newValue);
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="text-sm font-normal">
                                      {registrar.label}
                                    </FormLabel>
                                  </FormItem>
                                )}
                              />
                            ))}
                          </div>
                          <FormDescription>
                            Select specific registrars to exclude.
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              )}
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button type="submit" disabled={isPending} className="gap-2">
                {isPending
                  ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Generating...
                      </>
                    )
                  : (
                      <>
                        <Calculator className="size-4" />
                        Generate Report
                      </>
                    )}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>

      {reportData && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Report Results</CardTitle>
                <CardDescription>
                  Found
                  {' '}
                  {reportData.total_domains}
                  {' '}
                  domains in
                  {' '}
                  {reportData.total_batches}
                  {' '}
                  batches
                </CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <Input
                  type="search"
                  placeholder="Search data..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64"
                />
                <Button onClick={handleDownloadExcel} variant="outline" size="sm" disabled={!reportData || isPending}>
                  <FileSpreadsheet className="mr-2 size-4" />
                  Save Report
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="domains" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="domains">Domain Details</TabsTrigger>
                <TabsTrigger value="summary">Batch Summary</TabsTrigger>
                <TabsTrigger value="discrepancies">Discrepancies</TabsTrigger>
              </TabsList>

              <TabsContent value="domains" className="mt-6">
                <AGDataGrid
                  domLayout="autoHeight"
                  rowData={reportData.domains}
                  columnDefs={domainColumns}
                  quickFilterText={searchQuery}
                  overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No domains found</span>'
                />
              </TabsContent>

              <TabsContent value="summary" className="mt-6">
                <AGDataGrid
                  domLayout="autoHeight"
                  rowData={reportData.batch_summary}
                  columnDefs={summaryColumns}
                  quickFilterText={searchQuery}
                  overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No batches found</span>'
                />
              </TabsContent>

              <TabsContent value="discrepancies" className="mt-6">
                <AGDataGrid
                  domLayout="autoHeight"
                  rowData={discrepancyRows}
                  columnDefs={discrepancyColumns}
                  quickFilterText={searchQuery}
                  overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No discrepancies found</span>'
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
